import Model, {BudgetType, ModelSetting} from "../../../../model/Model";
import UserSetting, {IQuickStartSettings} from "../../../../model/UserSetting";
import {Box, Button, LinearProgress, Paper, TableContainer, Typography} from "@mui/material";
import React from "react";
import {NetWorthSection} from "../../networth/NetWorthView";
import {BudgetSection} from "../../budget/BudgetView";
import RoutesConfig from "../../../../RoutesConfig";
import {useNavigate} from "react-router-dom";
import {UpdateUserSettingInput} from "../../../../API";
import {useResources} from "../../../../stores/ResourceProvider";

const QuickStartWidget = ({
  model,
  quickStartUserSetting,
  showResume,
  onExit
} : {
  model: Model
  quickStartUserSetting?: UserSetting
  showResume?: boolean
  onExit(settings: IQuickStartSettings): void
}) => {

  const {userStore, confirm} = useResources()
  const navigate = useNavigate()

  const resumeQuickstart = async () => {
    if (quickStartUserSetting) {
      const quickStartSettings: IQuickStartSettings = quickStartUserSetting.settings
      if (quickStartSettings.status !== "Active") {
        quickStartSettings.status = "Active"
        const input: UpdateUserSettingInput = {
          id: quickStartUserSetting.id,
          settings: JSON.stringify(quickStartSettings)
        }
        await userStore.updateUserSetting(input)
      }

      const view = quickStartSettings.view
      if (view === "timeline") {
        navigate(`${RoutesConfig.calc.pathname}/timeline`)
      } else if (view === "networth") {
        navigate(`${RoutesConfig.calc.pathname}/networth`)
      } else if (view === "budget") {
        navigate(`${RoutesConfig.calc.pathname}/budget`)
      }
    }
  }

  const exitQuickstart = () => {
    if (quickStartUserSetting) {
      confirm.show('Confirm Exit', 'Do you want exit the Quick Start or resume later?',
        ['Exit', 'Resume Later'],
        () => {
          const quickStartSettings: IQuickStartSettings = quickStartUserSetting.settings
          onExit({...quickStartSettings, status: "Exited"})
          return true
        },
        () => {
          const quickStartSettings: IQuickStartSettings = quickStartUserSetting.settings
          onExit({...quickStartSettings, status: "Paused"})
          return true
        })
    }
  }

  const getQuickstartProgress = (settings: IQuickStartSettings) => {
    let stepNum = 0
    let stepCount = 0
    let progress = 0

    if (settings.status === "Completed") {
      progress = 100
    } else if (model) {
      const budgetType = model.getSetting(ModelSetting.BudgetType, BudgetType.Detailed)
      stepCount = (budgetType === BudgetType.Detailed) ? 15 : 11

      const {view, step} = settings

      if (view === "timeline") {
        stepNum = 0
      } else if (view === "networth") {
        switch (step) {
          case NetWorthSection.LiquidAssets:
            stepNum = 1
            break
          case NetWorthSection.RealEstateAssets:
            stepNum = 2
            break
          // case NetWorthSection.PersonalPropertyAssets:
          //   stepNum = 3
          //   break
          // case NetWorthSection.OtherAssets:
          //   stepNum = 4
          //   break
          case NetWorthSection.Liabilities:
            stepNum = 3
            break
        }
      } else if (view === "budget") {
        if (budgetType === BudgetType.Detailed) {
          switch (step) {
            case "Start":
              stepNum = 4
              break
            case BudgetSection.Income:
              stepNum = 5
              break
            case BudgetSection.Deductions:
              stepNum = 6
              break
            case BudgetSection.Taxes:
              stepNum = 7
              break
            case BudgetSection.Housing:
              stepNum = 8
              break
            case BudgetSection.FoodAndPersonal:
              stepNum = 9
              break
            case BudgetSection.InsuranceAndMedical:
              stepNum = 10
              break
            case BudgetSection.VehiclesAndTransportation:
              stepNum = 11
              break
            case BudgetSection.TravelAndEntertainment:
              stepNum = 12
              break
            case BudgetSection.GivingAndMisc:
              stepNum = 13
              break
            case "Summary":
              stepNum = 14
              break
          }
        } else {
          switch (step) {
            case "Start":
              stepNum = 4
              break
            case BudgetSection.Income:
              stepNum = 5
              break
            case BudgetSection.Deductions:
              stepNum = 6
              break
            case BudgetSection.Taxes:
              stepNum = 7
              break
            case BudgetSection.EssentialExpenses:
              stepNum = 8
              break
            case BudgetSection.DiscretionaryExpenses:
              stepNum = 9
              break
            case "Summary":
              stepNum = 10
              break
          }
        }
      }
      progress = Math.round((stepNum / stepCount) * 100)
    }

    return progress
  }

  if (!quickStartUserSetting) {
    return null
  }

  const quickStartSettings: IQuickStartSettings = quickStartUserSetting.settings
  if (quickStartSettings && quickStartSettings.status !== "Exited") {
    const progress = getQuickstartProgress(quickStartSettings)
    return (
      <TableContainer component={Paper} sx={{display:"flex", flexDirection:"row", alignItems:"center", mb:2, px:2, py:1}}>
        <Typography variant="h5" color="primary">Quick Start Status</Typography>
        <Box sx={{ display: 'flex', flexGrow:1,alignItems: 'center', ml:2 }}>
          <Box sx={{ width: '100%', mr: 1 }}>
            <LinearProgress variant="determinate" color="secondary" value={progress} />
          </Box>
          <Box sx={{ minWidth: 45 }}>
            <Typography variant="body2" color="text.secondary">
              {`${Math.round(progress,)}%`}
            </Typography>
          </Box>
        </Box>
        {showResume &&
          <Button variant="contained" size="small"color="primary" sx={{mr:2}}
                  onClick={resumeQuickstart}>
            Resume
          </Button>
        }
        <Button variant="outlined" size="small" color="primary"
                onClick={exitQuickstart}>
          Exit
        </Button>
      </TableContainer>
    )
  } else {
    return null
  }
}

export default QuickStartWidget
